// src/app/pages/orders_calendar/OrdersCalendar.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, Chip, CircularProgress, IconButton, MenuItem, Stack, TextField,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import moment from 'moment';
import 'moment/locale/es';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import orderServices from 'app/services/order-services';
import { ORDER_SOURCES } from 'app/utils/orderSources';
import { getDropoffCarrierDisplay } from 'app/utils/dropoffCarriers';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';

/**
 * Calendario de pedidos (solo superadmin por ahora): los pedidos de la compañia repartidos por su
 * fecha de despacho, para que quien organiza vea la carga de varios dias de una vez.
 *
 * Los pedidos no tienen hora, solo dia, y una compañia grande llega a mas de cien en un dia: el
 * mes muestra por dia cuantos hay, la mezcla de estados, los viajes y los que siguen sin
 * planificar. La semana trae los pedidos, agrupados por viaje, y deja reprogramar uno
 * arrastrandolo a otro dia (cambia su fecha de despacho). Los que ya van en un viaje no se
 * mueven desde aca: tienen la fecha del viaje.
 */

// Mismo orden y nombres que los rotulos de estado del resto de la web.
const ESTADOS = [
    { key: 'pending', label: 'Pendiente' },
    { key: 'invoiced', label: 'Documentado' },
    { key: 'picked', label: 'Pickeado' },
    { key: 'prepared', label: 'Preparado' },
    { key: 'in_progress', label: 'En progreso' },
    { key: 'finished', label: 'Finalizado' },
    { key: 'cancelled', label: 'Cancelado' },
    { key: 'deleted', label: 'Eliminado' },
];
const ESTADO_LABEL = Object.fromEntries(ESTADOS.map((e) => [e.key, e.label]));
const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const ISO = 'YYYY-MM-DD';

// Primer y ultimo dia de la grilla: semanas completas de lunes a domingo.
const rangoDe = (vista, ancla) => {
    if (vista === 'week') {
        const desde = moment(ancla).startOf('isoWeek');
        return [desde, desde.clone().add(6, 'days')];
    }
    return [moment(ancla).startOf('month').startOf('isoWeek'), moment(ancla).endOf('month').endOf('isoWeek')];
};

const OrdersCalendar = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();
    const { companySettings } = useCompanySettings() || {};
    const isSuperadmin = (authUser?.profiles || []).some((p) => p.name === 'superadmin');

    const [vista, setVista] = useState('month');
    const [ancla, setAncla] = useState(() => moment());
    const [estado, setEstado] = useState('');
    const [origen, setOrigen] = useState('');
    const [dias, setDias] = useState({});
    const [pedidos, setPedidos] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    // Dia abierto en el panel de la derecha (vista mes), con sus pedidos.
    const [diaAbierto, setDiaAbierto] = useState(null);
    const [pedidosDia, setPedidosDia] = useState([]);
    const [arrastrando, setArrastrando] = useState(null);
    const [sobre, setSobre] = useState(null);

    const [desde, hasta] = useMemo(() => rangoDe(vista, ancla), [vista, ancla]);
    const hoy = moment().format(ISO);
    // Sin filtro de estado no se cuentan los eliminados, igual que la carga que importa.
    const q = useMemo(() => ({ state_eq: estado, state_not_eq: estado ? '' : 'deleted', with_source: origen }), [estado, origen]);
    const colorDe = (key) => theme.palette[`state_${key}`]?.main || theme.palette.grey[400];

    const cargar = useCallback(() => {
        setCargando(true);
        setError(null);
        const params = { from: desde.format(ISO), to: hasta.format(ISO), q };
        const pedido = vista === 'week'
            ? orderServices.getCalendar({ ...params, detail: true }).then((d) => setPedidos(d.orders || []))
            : orderServices.getCalendar(params).then((d) => setDias(Object.fromEntries((d.days || []).map((x) => [x.date, x]))));
        pedido
            .catch((err) => setError(err?.response?.data?.error || 'No fue posible cargar el calendario'))
            .finally(() => setCargando(false));
    }, [vista, desde, hasta, q]);

    useEffect(() => { if (isSuperadmin) cargar(); }, [cargar, isSuperadmin]);

    const abrirDia = (fecha) => {
        setDiaAbierto(fecha);
        setPedidosDia([]);
        orderServices.getCalendar({ from: fecha, to: fecha, detail: true, q })
            .then((d) => setPedidosDia(d.orders || []))
            .catch((err) => enqueueSnackbar(err?.response?.data?.error || 'No fue posible cargar el día', { variant: 'error' }));
    };

    const mover = (pedido, fecha) => {
        setSobre(null);
        setArrastrando(null);
        if (!pedido || pedido.ship_date === fecha) return;
        // Se mueve en pantalla de inmediato; si el backend lo rechaza, vuelve a su dia.
        setPedidos((prev) => prev.map((o) => (o.id === pedido.id ? { ...o, ship_date: fecha } : o)));
        orderServices.moveInCalendar(pedido.id, fecha)
            .then(() => enqueueSnackbar(`#${pedido.code} reprogramado al ${moment(fecha).format('dddd D [de] MMMM')}`, { variant: 'success' }))
            .catch((err) => {
                setPedidos((prev) => prev.map((o) => (o.id === pedido.id ? { ...o, ship_date: pedido.ship_date } : o)));
                enqueueSnackbar(err?.response?.data?.error || 'No fue posible reprogramar el pedido', { variant: 'error' });
            });
    };

    const navegar = (paso) => setAncla((a) => a.clone().add(paso, vista === 'week' ? 'week' : 'month'));
    const titulo = vista === 'week'
        ? `${desde.format('D MMM')} – ${hasta.format('D MMM YYYY')}`
        : ancla.format('MMMM YYYY');

    if (!isSuperadmin) {
        return <Alert severity="info">El calendario de pedidos está disponible solo para superadmin.</Alert>;
    }

    const fechas = [];
    for (let d = desde.clone(); d.isSameOrBefore(hasta, 'day'); d.add(1, 'day')) fechas.push(d.format(ISO));

    // ---------- Vista mes: una celda por dia con conteos ----------
    const celdaMes = (fecha) => {
        const d = dias[fecha];
        const delMes = moment(fecha).month() === ancla.month();
        const atrasado = fecha < hoy && d?.unplanned > 0;
        return (
            <Box
                key={fecha}
                onClick={() => d && abrirDia(fecha)}
                sx={{
                    minHeight: 112, p: 1, borderRight: 1, borderBottom: 1, borderColor: 'divider',
                    bgcolor: diaAbierto === fecha ? 'action.selected' : 'background.paper',
                    opacity: delMes ? 1 : 0.45, cursor: d ? 'pointer' : 'default',
                    '&:hover': d ? { bgcolor: 'action.hover' } : {},
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="body2"
                        sx={fecha === hoy
                            ? { bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }
                            : { fontWeight: 600 }}
                    >
                        {moment(fecha).date()}
                    </Typography>
                    {d && <Typography variant="h5" sx={{ mb: 0, fontWeight: 700 }}>{d.total}</Typography>}
                </Stack>
                {d && (
                    <>
                        {/* Mezcla de estados: cada tramo proporcional a cuantos pedidos hay en ese estado. */}
                        <Tooltip arrow title={ESTADOS.filter((e) => d.by_state[e.key]).map((e) => `${e.label}: ${d.by_state[e.key]}`).join(' · ')}>
                            <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', my: 1 }}>
                                {ESTADOS.filter((e) => d.by_state[e.key]).map((e) => (
                                    <Box key={e.key} sx={{ flex: d.by_state[e.key], bgcolor: colorDe(e.key) }} />
                                ))}
                            </Box>
                        </Tooltip>
                        {d.unplanned > 0 && (
                            <Typography variant="caption" display="block" color={atrasado ? 'error.main' : 'text.secondary'} sx={{ fontWeight: atrasado ? 700 : 400 }}>
                                {d.unplanned} sin planificar
                            </Typography>
                        )}
                        {d.trips > 0 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                {d.trips} {d.trips === 1 ? 'viaje' : 'viajes'}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        );
    };

    // ---------- Tarjeta de pedido (semana y panel del dia) ----------
    const tarjeta = (o, arrastrable) => {
        const carrier = o.dropoff_carrier && getDropoffCarrierDisplay(o.dropoff_carrier, companySettings?.available_dropoff_carriers);
        const puedeMover = arrastrable && o.movable;
        return (
            <Box
                key={o.id}
                draggable={puedeMover}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setArrastrando(o); }}
                onDragEnd={() => { setArrastrando(null); setSobre(null); }}
                sx={{
                    p: 0.75, mb: 0.75, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 1,
                    borderLeft: `4px solid ${colorDe(o.state)}`, cursor: puedeMover ? 'grab' : 'default',
                    opacity: arrastrando?.id === o.id ? 0.4 : 1,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>#{o.code}</Typography>
                    {arrastrable && !o.movable && (
                        <Tooltip arrow title={o.trip ? 'Va en un viaje: se reprograma desde Viajes' : `Un pedido ${ESTADO_LABEL[o.state]?.toLowerCase()} no se reprograma`}>
                            <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </Tooltip>
                    )}
                </Stack>
                <Typography variant="caption" display="block" color="text.secondary" noWrap>{o.customer_name || 'Sin cliente'}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <Typography variant="caption" sx={{ color: colorDe(o.state), fontWeight: 600 }}>{ESTADO_LABEL[o.state] || o.state}</Typography>
                    {carrier?.label && <Typography variant="caption" color="text.secondary" noWrap>· {carrier.label}</Typography>}
                </Stack>
            </Box>
        );
    };

    // Pedidos de un dia agrupados por viaje; los que no tienen, al final.
    const agrupados = (lista) => {
        const grupos = {};
        lista.forEach((o) => {
            const k = o.trip ? o.trip.code : '';
            (grupos[k] = grupos[k] || []).push(o);
        });
        return Object.keys(grupos).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b))).map((k) => [k, grupos[k]]);
    };

    const encabezadoGrupo = (codigo, n) => (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, mb: 0.5 }}>
            {codigo && <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {codigo ? `Viaje ${codigo}` : 'Sin viaje'} ({n})
            </Typography>
        </Stack>
    );

    // ---------- Vista semana: una columna por dia ----------
    const columnaSemana = (fecha, i) => {
        const delDia = pedidos.filter((o) => o.ship_date === fecha);
        const destino = arrastrando && sobre === fecha && arrastrando.ship_date !== fecha;
        return (
            <Box
                key={fecha}
                onDragOver={(e) => { if (arrastrando) { e.preventDefault(); setSobre(fecha); } }}
                onDrop={(e) => { e.preventDefault(); mover(arrastrando, fecha); }}
                sx={{
                    p: 1, minHeight: 480, borderRight: i < 6 ? 1 : 0, borderColor: 'divider',
                    bgcolor: destino ? 'primary.light' : (fecha === hoy ? 'action.hover' : 'transparent'),
                    transition: 'background-color 0.15s',
                }}
            >
                <Stack alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">{DIAS_SEMANA[i]}</Typography>
                    <Typography variant="h4" sx={{ mb: 0, fontWeight: 700, color: fecha === hoy ? 'primary.main' : 'text.primary' }}>
                        {moment(fecha).date()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{delDia.length} pedidos</Typography>
                </Stack>
                {agrupados(delDia).map(([codigo, lista]) => (
                    <Box key={codigo || 'sin'}>
                        {encabezadoGrupo(codigo, lista.length)}
                        {lista.map((o) => tarjeta(o, true))}
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="h2" sx={{ mb: 0, mr: 1 }}>Calendario</Typography>
                <Button size="small" variant="outlined" onClick={() => setAncla(moment())}>Hoy</Button>
                <Stack direction="row" alignItems="center">
                    <IconButton size="small" onClick={() => navegar(-1)}><ChevronLeftIcon /></IconButton>
                    <IconButton size="small" onClick={() => navegar(1)}><ChevronRightIcon /></IconButton>
                </Stack>
                <Typography variant="h4" sx={{ mb: 0, textTransform: 'capitalize', minWidth: 200 }}>{titulo}</Typography>
                {cargando && <CircularProgress size={18} />}
                <Box sx={{ flex: 1 }} />
                <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {ESTADOS.map((e) => <MenuItem key={e.key} value={e.key}>{e.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Origen" value={origen} onChange={(e) => setOrigen(e.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {ORDER_SOURCES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
                </TextField>
                <ToggleButtonGroup size="small" exclusive value={vista} onChange={(e, v) => { if (v) { setVista(v); setDiaAbierto(null); } }}>
                    <ToggleButton value="month">Mes</ToggleButton>
                    <ToggleButton value="week">Semana</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {/* Leyenda de colores de estado */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {ESTADOS.filter((e) => e.key !== 'deleted').map((e) => (
                    <Chip key={e.key} size="small" variant="outlined" label={e.label}
                        icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorDe(e.key), ml: '6px !important' }} />} />
                ))}
                {vista === 'week' && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                        Arrastra un pedido a otro día para cambiar su fecha de despacho.
                    </Typography>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Card sx={{ flex: 1, overflow: 'hidden' }}>
                    {vista === 'month' ? (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: 1, borderColor: 'divider' }}>
                                {DIAS_SEMANA.map((d) => (
                                    <Typography key={d} variant="caption" color="text.secondary" sx={{ p: 1, textAlign: 'center', fontWeight: 600 }}>{d}</Typography>
                                ))}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                                {fechas.map(celdaMes)}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(140px, 1fr))', overflowX: 'auto' }}>
                            {fechas.map(columnaSemana)}
                        </Box>
                    )}
                </Card>

                {/* Panel del dia elegido en el mes */}
                {vista === 'month' && diaAbierto && (
                    <Card sx={{ width: 320, flexShrink: 0, p: 2, maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                            <Box>
                                <Typography variant="h5" sx={{ mb: 0, textTransform: 'capitalize' }}>{moment(diaAbierto).format('dddd D [de] MMMM')}</Typography>
                                <Typography variant="caption" color="text.secondary">{dias[diaAbierto]?.total || 0} pedidos</Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setDiaAbierto(null)}><CloseIcon fontSize="small" /></IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => { setAncla(moment(diaAbierto)); setVista('week'); setDiaAbierto(null); }}>Ver semana</Button>
                            <Button size="small" variant="outlined" onClick={() => navigate('/planification')}>Planificar</Button>
                        </Stack>
                        {pedidosDia.length === 0 ? <CircularProgress size={18} /> : agrupados(pedidosDia).map(([codigo, lista]) => (
                            <Box key={codigo || 'sin'}>
                                {encabezadoGrupo(codigo, lista.length)}
                                {lista.map((o) => tarjeta(o, false))}
                            </Box>
                        ))}
                    </Card>
                )}
            </Stack>
        </Box>
    );
};

export default OrdersCalendar;
